import { GET_CLIENT_DIRECTORY, lawyerDirectoryService } from '@/api';
import { useFlagReasons, useNotification } from '@/hooks';
import * as React from 'react';
import { BaseCaseFilters, BulkFlagClient } from '@/interfaces';
import { Button } from '../common';
import { useQueryClient } from '@tanstack/react-query';
import { FlagReasonOption, FlagReasonsPicker } from './FlagReasonsPicker';

interface FlagClientsBulkModalProps {
	caseId: string;
	userIds: (string | null)[];
	parsedFilters?: BaseCaseFilters;
	hide: () => void;
	onSuccess: () => void;
	onDeleteModalOpenChange?: (isOpen: boolean) => void;
}

export const FlagClientsBulkModal: React.FC<FlagClientsBulkModalProps> = ({
	caseId,
	userIds,
	parsedFilters,
	hide,
	onSuccess,
	onDeleteModalOpenChange,
}) => {
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
	const [pickerView, setPickerView] = React.useState<'list' | 'create'>('list');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
	const { toastSuccess, toastError } = useNotification();
	const queryClient = useQueryClient();
	const { flagReasons, isLoading, refetch } = useFlagReasons(caseId);

	const options: FlagReasonOption[] = React.useMemo(
		() =>
			flagReasons.map((reason) => ({
				id: reason.id,
				label: reason.name,
				isCustom: reason.is_custom,
				description: reason.description || undefined,
			})),
		[flagReasons]
	);

	const handleCreateReason = async ({
		label,
		description,
	}: {
		label: string;
		description?: string;
	}) => {
		try {
			await lawyerDirectoryService.postFlagReason(caseId, label, description);
			await refetch();
			setSelectedReasons((prev) =>
				prev.includes(label) ? prev : [...prev, label]
			);
			toastSuccess('A reason has been successfully added to the list.');
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const handleEditReason = async (
		previousLabel: string,
		reason: { id?: number; label: string; description?: string }
	) => {
		if (!reason.id) return;
		try {
			await lawyerDirectoryService.putFlagReason(
				reason.id,
				reason.label,
				reason.description
			);
			await refetch();
			setSelectedReasons((prev) =>
				prev.map((label) => (label === previousLabel ? reason.label : label))
			);
			toastSuccess('The change has been saved successfully.');
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const handleDeleteReason = async (option: FlagReasonOption) => {
		if (!option.id) return;
		try {
			await lawyerDirectoryService.deleteFlagReason(option.id);
			await refetch();
			setSelectedReasons((prev) =>
				prev.filter((label) => label !== option.label)
			);
			toastSuccess('The flag has been successfully removed.');
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const onSubmit = async () => {
		if (isSubmitting || selectedReasons.length === 0) return;
		try {
			setIsSubmitting(true);
			const newData: BulkFlagClient = {
				...parsedFilters,
				caseId,
				users_ids: userIds.includes(null) ? [] : (userIds as string[]),
				reasons: selectedReasons,
				flagged: true,
			};
			await lawyerDirectoryService.postBulkFlagClient(newData);
			toastSuccess('Clients flagged for review successfully.');
			hide();
			onSuccess();
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_DIRECTORY] });
		} catch (error) {
			toastError('Couldnt flag clients for review. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="space-y-8">
			<div>
				<FlagReasonsPicker
					options={options}
					selected={selectedReasons}
					onChange={setSelectedReasons}
					onCreateReason={handleCreateReason}
					onEditReason={handleEditReason}
					onDeleteReason={handleDeleteReason}
					onRefreshReasons={refetch}
					onViewChange={setPickerView}
					onDeleteModalOpenChange={(isOpen) => {
						setIsDeleteModalOpen(isOpen);
						onDeleteModalOpenChange?.(isOpen);
					}}
					loading={isLoading}
					disabled={isSubmitting}
				/>
			</div>
			{pickerView === 'list' && !isDeleteModalOpen && (
				<div className="flex items-center justify-center gap-x-2">
					<Button
						label="Confirm"
						size="full"
						decoration="primary"
						type="button"
						onClick={onSubmit}
						disabled={isSubmitting || selectedReasons.length === 0}
					/>
				</div>
			)}
		</div>
	);
};
